import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, CreditCard, MessageSquare, Calendar, Gift, Bell,
  TrendingUp, Shield, Check, ArrowRight, Star, Clock,
  FileText, Percent, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ClienteJaDemo = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Users,
      title: 'Cadastro Completo',
      description: 'Nome, CPF/CNPJ, nascimento, endereço, telefone. Tudo organizado.'
    },
    {
      icon: CreditCard,
      title: 'Cobrança Automática',
      description: 'Gere boleto e Pix. Parcele como quiser. Sem esquecer ninguém.'
    },
    {
      icon: Bell,
      title: 'Lembretes Inteligentes',
      description: '3 dias antes, no dia, e aviso gentil de atraso. Tudo automático.'
    },
    {
      icon: Gift,
      title: 'Datas Comemorativas',
      description: 'Aniversário, Natal, Ano Novo. Mensagens que fidelizam.'
    },
    {
      icon: Percent,
      title: 'Descontos Fáceis',
      description: 'Aplique descontos manuais ou automáticos em campanhas.'
    },
    {
      icon: TrendingUp,
      title: 'Relatório de Inadimplência',
      description: 'Saiba quem está em dia e quem precisa de atenção.'
    }
  ];

  const templates = [
    { trigger: '3 dias antes', preview: '"Olá, {NOME}. Passando pra te lembrar que o pagamento vence em {DATA}..."' },
    { trigger: 'No dia', preview: '"Oi, {NOME}. Hoje é o vencimento de {PARCELA}. Se já pagou, pode ignorar 🙏"' },
    { trigger: 'Atraso (suave)', preview: '"Oi, {NOME}. Vi que o pagamento ainda aparece pendente. Quer um novo prazo?"' },
    { trigger: 'Aniversário', preview: '"{NOME}! Feliz aniversário 🎉 Deixei um carinho: {DESCONTO} válido até {DATA}."' },
    { trigger: 'Natal', preview: '"Feliz Natal, {NOME}! 🎄 Obrigado pela confiança. Bônus: {DESCONTO}."' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-teal-50 via-white to-cyan-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full text-teal-700 text-sm font-medium mb-6">
            <Users size={16} />
            Gestão de Clientes Simplificada
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Seus clientes{' '}
            <span className="text-teal-600">organizados</span>,
            <br />suas cobranças{' '}
            <span className="text-teal-600">em dia</span>
          </h1>
          
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Cadastre clientes, cobre automaticamente, envie lembretes gentis e 
            fidelize com mensagens em datas especiais. Tudo pelo WhatsApp.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={() => navigate('/clienteja/configurar')}
              className="bg-teal-600 hover:bg-teal-700 text-lg px-8 py-6"
            >
              Começar grátis
              <ArrowRight size={20} />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/clienteja/demo')}
              className="text-lg px-8 py-6"
            >
              Ver demonstração
            </Button>
          </div>

          <div className="flex items-center justify-center gap-8 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Check size={16} className="text-teal-500" />
              Até 50 clientes grátis
            </span>
            <span className="flex items-center gap-2">
              <Check size={16} className="text-teal-500" />
              Sem cartão de crédito
            </span>
            <span className="flex items-center gap-2">
              <Check size={16} className="text-teal-500" />
              LGPD compliant
            </span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Tudo que você precisa para gerenciar clientes
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-2xl border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center mb-4">
                  <feature.icon className="text-teal-600" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
            Mensagens prontas que funcionam
          </h2>
          <p className="text-slate-600 text-center mb-12">
            Templates testados, sem gatilhos que bloqueiam. Só clicar e enviar.
          </p>
          
          <div className="space-y-4">
            {templates.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-slate-200 flex items-start gap-4">
                <div className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium whitespace-nowrap">
                  {t.trigger}
                </div>
                <p className="text-slate-600 italic">{t.preview}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LGPD */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-8 text-white">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Shield size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">LGPD Compliant</h3>
                <p className="text-teal-100 mb-4">
                  Cada cliente autoriza o recebimento de mensagens. 
                  Guardamos logs de tudo para sua proteção jurídica.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check size={16} />
                    Checkbox de consentimento no cadastro
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={16} />
                    Log de todas as mensagens enviadas
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={16} />
                    Máximo 1 mensagem por cliente por dia
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            Preço justo para crescer
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Básico */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Básico</h3>
              <p className="text-slate-500 mb-4">Para começar</p>
              <div className="text-4xl font-bold text-slate-900 mb-6">
                R$ 49<span className="text-lg text-slate-400">,99/mês</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  <strong>Até 50 clientes</strong>
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  Cobranças ilimitadas
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  Lembretes automáticos
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  Templates prontos
                </li>
              </ul>
              <Button onClick={() => navigate('/clienteja/configurar')} className="w-full bg-teal-600 hover:bg-teal-700">
                Começar agora
              </Button>
            </div>

            {/* Crescimento */}
            <div className="bg-white rounded-2xl p-6 border-2 border-teal-500 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                MAIS POPULAR
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Crescimento</h3>
              <p className="text-slate-500 mb-4">Para escalar</p>
              <div className="text-4xl font-bold text-slate-900 mb-6">
                R$ 89<span className="text-lg text-slate-400">,99/mês</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  <strong>Até 200 clientes</strong>
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  Tudo do Básico
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  Campanhas de desconto
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  Relatórios avançados
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check size={16} className="text-teal-500" />
                  Suporte prioritário
                </li>
              </ul>
              <Button onClick={() => navigate('/clienteja/configurar?plano=crescimento')} className="w-full bg-teal-600 hover:bg-teal-700">
                Escolher Crescimento
              </Button>
            </div>

            {/* Pro */}
            <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl p-6 text-white relative">
              <div className="absolute -top-3 right-6 bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">
                COMPLETO
              </div>
              <h3 className="text-xl font-semibold mb-2">AtivaJá Pro</h3>
              <p className="text-teal-100 mb-4">Tudo em um só lugar</p>
              <div className="text-4xl font-bold mb-6">
                R$ 199<span className="text-lg text-teal-200">,99/mês</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <Check size={16} />
                  <strong>Clientes ILIMITADOS</strong>
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} />
                  Tudo do Crescimento
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} />
                  + Portfólio profissional
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} />
                  + Orçamento automático
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} />
                  + Agendamento online
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} />
                  + PostaJá (48 conteúdos IA)
                </li>
              </ul>
              <Button onClick={() => navigate('/pro')} className="w-full bg-white text-teal-600 hover:bg-teal-50">
                Ver AtivaJá Pro
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Chega de planilha e anotação em papel
          </h2>
          <p className="text-slate-600 mb-8">
            Seus clientes merecem atenção. Você merece organização.
          </p>
          <Button 
            onClick={() => navigate('/clienteja/configurar')}
            size="lg"
            className="bg-teal-600 hover:bg-teal-700 text-lg px-8"
          >
            <Users size={20} />
            Começar a organizar
            <ArrowRight size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClienteJaDemo;
